import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getContentByType, getContentFromCache, getTranslation, hasTranslation, ContentDocument } from '../api/contentApi';

const BlogListPage: React.FC = () => {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'de';

  const navigate = useNavigate();
  const [posts, setPosts] = useState<ContentDocument[]>(() => getContentFromCache('blog') ?? []);
  const [loading, setLoading] = useState(() => getContentFromCache('blog') === null);
  const [error, setError] = useState('');

  useEffect(() => {
    getContentByType('blog')
      .then(setPosts)
      .catch(() => setError(t('common.error')))
      .finally(() => setLoading(false));
  }, [t]);

  // Only show posts that have a published translation in the current language
  const visiblePosts = posts
    .filter((post) => hasTranslation(post, currentLang))
    .sort((a, b) => {
      // Highlighted post always first
      if (a.isHighlighted && !b.isHighlighted) return -1;
      if (!a.isHighlighted && b.isHighlighted) return 1;
      // Then by sortOrder descending (highest = top)
      const orderDiff = (b.sortOrder ?? 0) - (a.sortOrder ?? 0);
      if (orderDiff !== 0) return orderDiff;
      // Fallback: newest date first
      const dateA = a.publicationDate || a.createdAt;
      const dateB = b.publicationDate || b.createdAt;
      return dateB.localeCompare(dateA);
    });

  if (loading) return <div className="container"><p>{t('common.loading')}</p></div>;
  if (error) return <div className="container"><div className="error">{error}</div></div>;

  const [featured, ...rest] = visiblePosts;

  return (
    <div>
      {/* Hero section — 100vh full bleed. TODO: replace with url('/images/blog.jpg') */}
      <section
        className="hero-section"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/cd4d63_7c77e61da6b340baa7c7295fa3040632~mv2.avif)` }}
      >
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1>{t('nav.blog')}</h1>
        </div>
      </section>

      <div className="blog-page-container">
        {visiblePosts.length === 0 ? (
          <p className="text-gray-500 py-12">
            {currentLang === 'de' ? 'Noch keine Blogbeiträge.' : currentLang === 'fr' ? 'Pas encore d\'articles.' : 'No blog posts yet.'}
          </p>
        ) : (
          <>
            {/* Featured (newest) post */}
            {featured && (() => {
              const tr = getTranslation(featured, currentLang)!;
              const slug = tr.slug || featured.slug || featured.id;
              return (
                <Link to={`/${currentLang}/blog/${slug}`} className="blog-featured" key={featured.id}>
                  {featured.featuredImageUrl ? (
                    <img src={featured.featuredImageUrl} alt={tr.title || ''} className="blog-featured-img" loading="eager" />
                  ) : (
                    <div className="blog-featured-img blog-featured-img--placeholder" />
                  )}
                  <div className="blog-featured-body">
                    {featured.tags && featured.tags.length > 0 && (
                      <span className="blog-tag">{featured.tags[0]}</span>
                    )}
                    <h2 className="blog-featured-title">{tr.title}</h2>
                    {tr.excerpt && <p className="blog-featured-excerpt">{tr.excerpt}</p>}
                    <div className="blog-meta">
                      {featured.publicationDate && (
                        <span>{new Date(featured.publicationDate).toLocaleDateString(currentLang, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                      )}
                      {featured.author && (
                        <span
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/${currentLang}/portrait`); }}
                          className="hover:underline cursor-pointer"
                        >
                          {t('common.by')} {featured.author}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })()}

            {/* Grid of remaining posts */}
            {rest.length > 0 && (
              <div className="blog-grid">
                {rest.map((post) => {
                  const tr = getTranslation(post, currentLang)!;
                  const slug = tr.slug || post.slug || post.id;
                  return (
                    <Link to={`/${currentLang}/blog/${slug}`} className="blog-card" key={post.id}>
                      {post.featuredImageUrl ? (
                        <img src={post.featuredImageUrl} alt={tr.title || ''} className="blog-card-img" loading="lazy" />
                      ) : (
                        <div className="blog-card-img blog-card-img--placeholder" />
                      )}
                      <div className="blog-card-body">
                        {post.tags && post.tags.length > 0 && (
                          <span className="blog-tag">{post.tags[0]}</span>
                        )}
                        <h3 className="blog-card-title">{tr.title}</h3>
                        {tr.excerpt && <p className="blog-card-excerpt">{tr.excerpt}</p>}
                        <div className="blog-meta">
                          {post.publicationDate && (
                            <span>{new Date(post.publicationDate).toLocaleDateString(currentLang, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                          )}
                          {post.author && (
                            <span
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/${currentLang}/portrait`); }}
                              className="hover:underline cursor-pointer"
                            >
                              {t('common.by')} {post.author}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BlogListPage;
